import type { MaybeRefOrGetter, Ref } from 'vue'
import type { SectionStatuses } from '~/store-v2/financings/useFinancing'
import type { StageFormalization } from '~/ui-blocks/Formalization/Formalization.types'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import { useTerminalStatusLock } from '~/composables/financing/useTerminalStatusLock'
import { useDateProps } from '~/composables/proposal/useDateProps'
import { PaymentModelStatus } from '~/constants/receiptModel'
import useFinancing from '~/store-v2/financings/useFinancing'
import { FinancingStatus } from '~/store-v2/financings/useFinancings'
import { useEquipmentEditStore } from '~/store/equipmentEdit'
import { Formalization } from '~/types/enum'
import { getStatusTag } from '~/ui-blocks/Formalization/Formalization.utils'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'
import { isFieldLockingStepStatus } from '~/utils/receipt-model/receiptModelFlow'

export interface UsePaymentModelAccessOptions {
  projectId: Ref<string>
  statusProject: MaybeRefOrGetter<SectionStatuses | undefined>
  stage: MaybeRefOrGetter<StageFormalization>
  stageReproved: MaybeRefOrGetter<string>
}

const FINISHED_FINANCING_STATUSES: ReadonlySet<string> = new Set<string>([
  FinancingStatus.approved,
  FinancingStatus.reproved,
  FinancingStatus.expired,
  FinancingStatus.canceled,
])

export function usePaymentModelAccess(options: UsePaymentModelAccessOptions) {
  const { projectId } = options
  const { t } = useI18n()
  const equipmentEditStore = useEquipmentEditStore()

  const { data: financing, refetch: refetchFinancing } = useFinancing(projectId)
  const { isTerminalLocked, isTerminalReadOnly, terminalTag } = useTerminalStatusLock(financing, 'payment_model', t)
  const { hasPendingTAF } = usePendingUpdateEquipment(projectId)

  const blocked = ref(true)

  const statusProject = computed(() => toValue(options.statusProject))

  const stepStatus = computed<string | null>(() => statusProject.value?.payment_model?.status ?? null)

  const enablePooling = computed(() => statusProject.value?.biometry_and_contract?.status === GeneralStatuses.APPROVED)

  const isFinancingFinished = computed(() => !!financing.value && FINISHED_FINANCING_STATUSES.has(financing.value.status))
  const isApprovedPaymentModel = computed(() => financing.value?.section_statuses?.payment_model?.status === PaymentModelStatus.APPROVED)

  // Terminal financing + started (or approved) step: viewing wins over cross-step blockers
  // (stage reproved, pending TAF, equipment edit in progress). Requires the payment model
  // query, which only runs once the contract is approved; otherwise the body would be empty.
  const isTerminalViewable = computed(() =>
    isTerminalReadOnly.value && !isTerminalLocked.value && enablePooling.value,
  )
  // Terminal padlock: step never started, or started while the payment model query is off.
  const isTerminalClosed = computed(() => isTerminalReadOnly.value && !isTerminalViewable.value)

  const isBlockedByPendingEquipment = computed(() =>
    hasPendingTAF.value && !isApprovedPaymentModel.value && !isTerminalViewable.value,
  )
  const isInterruptedByEquipmentEdit = computed(() =>
    equipmentEditStore.isProjectBeingEdited(projectId.value)
    && !isApprovedPaymentModel.value
    && !isTerminalViewable.value,
  )
  const canOpenStep = computed(() => financing.value?.openable_steps?.receiptModel)

  const isFieldLocked = computed(() => isFinancingFinished.value || isFieldLockingStepStatus(stepStatus.value))

  const isEquipmentApproved = computed(() =>
    statusProject.value?.equipment?.status === GeneralStatuses.APPROVED
    || financing.value?.section_statuses?.equipment?.status === GeneralStatuses.APPROVED,
  )
  const isSendDisabled = computed(() => isFieldLocked.value || !isEquipmentApproved.value)

  const hasShownStatus = computed(() =>
    !isBlockedByPendingEquipment.value && (isApprovedPaymentModel.value || isFinancingFinished.value),
  )

  const submittedAt = computed(() => financing.value?.section_statuses?.payment_model?.submitted_at)
  const completedAt = computed(() => financing.value?.section_statuses?.payment_model?.completed_at)
  const reprovedAt = computed(() => financing.value?.section_statuses?.payment_model?.reproved_at)
  // `blocked` (from checkStageReproved) is ignored while the step is terminal-viewable (D2).
  const blockedForView = computed(() => blocked.value && !isTerminalViewable.value)

  const dateProps = useDateProps('receiptModel', { completedAt, reprovedAt, submittedAt, blocked: blockedForView, financing })
  const showDateProps = computed(() => Boolean(submittedAt.value || completedAt.value || reprovedAt.value || !hasShownStatus.value))

  const isBlocked = computed(() =>
    isInterruptedByEquipmentEdit.value
    || blockedForView.value
    || isTerminalClosed.value
    || !canOpenStep.value
    || isBlockedByPendingEquipment.value,
  )

  const openCollapse = computed(() =>
    (canOpenStep.value || (!isBlocked.value && toValue(options.stage) === Formalization.PAYMENT_MODEL))
    && !isBlockedByPendingEquipment.value
    && !isInterruptedByEquipmentEdit.value
    && !isTerminalClosed.value,
  )

  const tag = computed(() => {
    // Terminal financing: every non-approved step shows the terminal tag, open or locked (D3).
    if (terminalTag.value)
      return terminalTag.value

    if (isBlockedByPendingEquipment.value)
      return undefined

    return blockedForView.value || !canOpenStep.value
      ? undefined
      : getStatusTag(t, stepStatus.value ?? undefined)
  })

  function checkStageReproved() {
    const stageReproved = toValue(options.stageReproved)
    const isContractBlocked = statusProject.value?.biometry_and_contract?.status !== GeneralStatuses.APPROVED

    if (stageReproved === '' && !isContractBlocked)
      blocked.value = false
    else
      blocked.value = stageReproved !== Formalization.PAYMENT_MODEL || isContractBlocked
  }

  async function refreshAccess() {
    await refetchFinancing()

    if (financing.value?.openable_steps?.contract)
      blocked.value = false

    checkStageReproved()
  }

  return {
    financing,
    refetchFinancing,
    refreshAccess,
    stepStatus,
    enablePooling,
    isFieldLocked,
    isSendDisabled,
    isTerminalReadOnly,
    isTerminalViewable,
    hasShownStatus,
    showDateProps,
    dateProps,
    submittedAt,
    completedAt,
    reprovedAt,
    isBlocked,
    openCollapse,
    tag,
    showPendingEquipmentTag: isBlockedByPendingEquipment,
  }
}
